import contentCreationService from '@/services/contentCreationService';
import { useScrapeControlStore } from '@/store/scrapeControlStore';
import { useNotificationStore } from '@/store/notificationStore';
import { simpleSemaphore } from '@/helpers/simpleSemaphore';
import { delay } from '@/utils/delay';
import { createAbortFetchResult } from '@/factories/createAbortFetchResult';
import { createErrorFetchResult } from '@/factories/createErrorFetchResult';
import IRawPageSourceFetchResult from '@/interfaces/IRawPageSourceFetchResult';
import { DEFAULT_WAIT_MS } from '@/constants/DefaultWaitMs';
import IPageSourceResponse from '@/interfaces/IPageSourceResponse';

const semaphore = new simpleSemaphore(1); // only one concurrent fetch

export async function fetchPageSourceByUrl(
    url: string,
): Promise<IRawPageSourceFetchResult> {
    const scrapeControlStore = useScrapeControlStore();
    const notificationStore = useNotificationStore();
    const initialResetCounter = scrapeControlStore.resetCounter;   // snapshot of the ref value at the start to detect Reset as a guard clause

    /* 1 acquire slot making is so the fetch will wait until a slot is available. 
    If set to 1 only one fetch can be done at once increasing the number at the top will allow multiple fetches at once */
    await semaphore.acquire();

    try {
        /* 2 honour Pause / Reset BEFORE firing request */
        while (scrapeControlStore.isPaused || initialResetCounter !== scrapeControlStore.resetCounter) {
            if (initialResetCounter !== scrapeControlStore.resetCounter) { // if a reset has happened the resetCounter will increase causing the loop to break
                return createAbortFetchResult(); // aborted by Reset
            }
            await delay(DEFAULT_WAIT_MS); // wait for a bit before checking again 
        }

        /* 3 perform the real HTTP call */
        scrapeControlStore.startFetch(); // lets the store know a fetch is in progress

        try {
            // 2) call API, typed to your IPageSourceResponse
            const { data: pageSourceJsonRef, error: fetchError } =
                await contentCreationService
                    .getPageSourceByUrl(url)
                    .json<IPageSourceResponse>();

            // 3) transport‐level error?
            if (fetchError.value) {
                notificationStore.addErrorNotification(
                    `Network error for ${url}: ${fetchError.value}`
                );
                return createErrorFetchResult();
            }

            const pageSourceResponse = pageSourceJsonRef.value;
            // 4) top‐level status check
            if (!pageSourceResponse || pageSourceResponse.status !== 'success') {
                notificationStore.addErrorNotification(
                    `API error for ${url}: ${JSON.stringify(pageSourceResponse)}`
                );
                return createErrorFetchResult();
            }

            // 5) validate payload structure
            const { httpStatusCode, httpResponse } = pageSourceResponse.data;
            if (typeof httpStatusCode !== 'number') {
                notificationStore.addErrorNotification(
                    `Validation error for ${url}: Expected 'httpStatusCode' to be a number, but received ${JSON.stringify(pageSourceResponse.data)}`
                );
                return createErrorFetchResult();
            }

            if (typeof httpResponse !== 'string') {
                notificationStore.addErrorNotification(
                    `Validation error for ${url}: Expected 'httpResponse' to be a string, but received ${JSON.stringify(pageSourceResponse.data)}`
                );
                return createErrorFetchResult();
            }

            // 6) re‐serialize for downstream JSON.parse
            return {
                data: JSON.stringify(pageSourceResponse),
                statusCode: httpStatusCode,
            };
        } catch (err) {
            notificationStore.addErrorNotification(
                `Fetch/parsing error for ${url}: ${err}`
            );
            return createErrorFetchResult();
        }
    } finally {
        /* 4 always clean up */
        scrapeControlStore.finishFetch(); // lets the store know a fetch has finished 
        semaphore.release();
    }
}
